import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { Menu, MenuModule } from 'primeng/menu';
import { CesCheckableMenuItem, CesCheckableMenuItemDirective } from './ces-checkable-menu.directive';

@Component({
  selector: 'ces-checkable-menu',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, MenuModule, CesCheckableMenuItemDirective, CheckboxModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ces-checkable-menu.component.html',
  styleUrl: './ces-checkable-menu.component.scss',
})
export class CesCheckableMenuComponent implements AfterContentInit {
  @Input() model: (MenuItem | CesCheckableMenuItem)[] = [];
  @Input() popup: boolean = false;
  @Input() style: { [klass: string]: unknown } | null | undefined;
  @Input() styleClass: string = '';
  @Input() appendTo: HTMLElement | ElementRef | TemplateRef<unknown> | string | null | undefined;
  @Input() autoZIndex: boolean = true;
  @Input() baseZIndex: number = 0;
  @Input() showTransitionOptions: string = '.12s cubic-bezier(0, 0, 0.2, 1)';
  @Input() hideTransitionOptions: string = '.1s linear';

  @ViewChild('menu') menu!: Menu;

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined;

  @ContentChild('start', { descendants: false }) startTemplate: TemplateRef<unknown> | null = null;
  _startTemplate: TemplateRef<unknown> | null = null;

  @ContentChild('end', { descendants: false }) endTemplate: TemplateRef<unknown> | null = null;
  _endTemplate: TemplateRef<unknown> | null = null;

  ngAfterContentInit(): void {
    this.templates?.forEach(item => {
      switch (item.getType()) {
        case 'start':
          this._startTemplate = item.template;
          break;
        case 'end':
          this._endTemplate = item.template;
          break;
      }
    });
  }

  isCheckable(item: MenuItem | CesCheckableMenuItem): boolean {
    return 'checkable' in item && item.checkable === true;
  }

  getCheckedItems(): CesCheckableMenuItem[] {
    return this.extractCheckedItems(this.model);
  }

  getCheckedItemInGroup(groupName: string): CesCheckableMenuItem | null {
    const items = this.extractCheckedItems(this.model);
    return items.find(item => item.exclusiveGroup === groupName) ?? null;
  }

  private extractCheckedItems(items: CesCheckableMenuItem[]): CesCheckableMenuItem[] {
    const result: CesCheckableMenuItem[] = [];

    if (!items || !Array.isArray(items)) return result;

    items.forEach(item => {
      if (item.items && Array.isArray(item.items)) {
        result.push(...this.extractCheckedItems(item.items));
      }

      if (item.checkable && item.checked) {
        result.push(item);
      }
    });

    return result;
  }

  toggle(event: Event): void {
    if (this.menu) {
      this.menu.toggle(event);
    }
  }

  show(event: Event): void {
    if (this.menu) {
      this.menu.show(event);
    }
  }

  hide(): void {
    if (this.menu) {
      this.menu.hide();
    }
  }
}
